import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Videocam as VideocamIcon,
  Add as AddIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import PageHeader from "../../components/PageHeader";
import ApiService from "../../services/api-service";
import notifier from "../../components/Notifier";
import { useTranslation } from "react-i18next";
import { getTimeDue } from "../../constants/function";
import ConfirmDialog from "../../components/ConfirmDialog";

export default function StreamKeys() {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [serverUrl, setServerUrl] = useState("");
  const [obsAvailable, setObsAvailable] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [keyConfigs, setKeyConfigs] = useState({});
  const [loadingConfig, setLoadingConfig] = useState({});

  // Create key dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState("");
  const [keyLabel, setKeyLabel] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [loadingStudios, setLoadingStudios] = useState(false);

  // OBS API Key state
  const [obsApiKey, setObsApiKey] = useState(null);
  const [obsApiKeyVisible, setObsApiKeyVisible] = useState(false);
  const [loadingObsApiKey, setLoadingObsApiKey] = useState(true);
  const [generatingObsApiKey, setGeneratingObsApiKey] = useState(false);
  const [revokeObsApiKeyDialogOpen, setRevokeObsApiKeyDialogOpen] = useState(false);

  useEffect(() => {
    checkStatus();
    fetchKeys();
    fetchStudios();
    fetchObsApiKey();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await ApiService.STREAM_KEYS_STATUS();
      const data = response.result || response;
      setObsAvailable(data.available);
      if (data.serverUrl) {
        setServerUrl(data.serverUrl);
      }
    } catch (error) {
      console.error("Error checking OBS status:", error);
      setObsAvailable(false);
    }
  };

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const response = await ApiService.STREAM_KEYS_LIST({ activeOnly: true });
      const data = response.result || response;
      setKeys(data.keys || []);
      if (data.serverUrl) {
        setServerUrl(data.serverUrl);
      }
    } catch (error) {
      console.error("Error fetching stream keys:", error);
      notifier.error(t("streamKeys.fetchError") || "Failed to load stream keys");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudios = async () => {
    try {
      setLoadingStudios(true);
      const response = await ApiService.TOPTEN_MY_PLAYLIST({ pageNo: 1, pageSize: 100 });
      const playlists = response?.result || (Array.isArray(response) ? response : []);
      setStudios(playlists);
    } catch (error) {
      console.error("Error fetching studios:", error);
      setStudios([]);
    } finally {
      setLoadingStudios(false);
    }
  };

  const fetchObsApiKey = async () => {
    try {
      setLoadingObsApiKey(true);
      const response = await ApiService.OBS_API_KEY_GET();
      const data = response.result || response;
      if (data.apiKey) {
        setObsApiKey({
          key: data.apiKey,
          createdAt: data.createdAt,
        });
      } else {
        setObsApiKey(null);
      }
    } catch (error) {
      console.error("Error fetching OBS API key:", error);
      setObsApiKey(null);
    } finally {
      setLoadingObsApiKey(false);
    }
  };

  const handleGenerateObsApiKey = async () => {
    try {
      setGeneratingObsApiKey(true);
      const response = await ApiService.OBS_API_KEY_GENERATE();
      const data = response.result || response;
      setObsApiKey({
        key: data.apiKey,
        createdAt: data.createdAt,
      });
      setObsApiKeyVisible(true);
      notifier.success(t("obsApiKey.generateSuccess") || "OBS API key generated successfully");
    } catch (error) {
      console.error("Error generating OBS API key:", error);
      notifier.error(error.message || t("obsApiKey.generateError") || "Failed to generate OBS API key");
    } finally {
      setGeneratingObsApiKey(false);
    }
  };

  // Open one dock as a free-floating browser window. Each dock has its own
  // button so each click is a separate user gesture — opening all three in
  // one click triggers Chrome's popup blocker on the 2nd/3rd window.
  const openDockWindow = (path, name, features) => {
    if (!obsApiKey?.key) {
      notifier.error(t("obsApiKey.popOutMissingKey") || "Generate an OBS API key first");
      return;
    }
    const url = `${window.location.origin}/obs/${path}?apiKey=${encodeURIComponent(obsApiKey.key)}`;
    const win = window.open(url, name, features);
    if (!win) {
      notifier.error(
        t("obsApiKey.popOutBlocked") ||
          "Your browser blocked the popup. Allow popups for this site and try again.",
      );
    }
  };

  const handleRevokeObsApiKey = async () => {
    try {
      await ApiService.OBS_API_KEY_REVOKE();
      setObsApiKey(null);
      setObsApiKeyVisible(false);
      notifier.success(t("obsApiKey.revokeSuccess") || "OBS API key revoked successfully");
    } catch (error) {
      console.error("Error revoking OBS API key:", error);
      notifier.error(error.message || t("obsApiKey.revokeError") || "Failed to revoke OBS API key");
    } finally {
      setRevokeObsApiKeyDialogOpen(false);
    }
  };

  const handleCreateKey = async () => {
    if (!selectedStudio) {
      notifier.error(t("streamKeys.studioRequired") || "Please select a studio first");
      return;
    }

    try {
      setCreatingKey(true);
      const studio = studios.find((s) => s._id === selectedStudio);

      const response = await ApiService.STREAM_KEYS_CREATE({
        channelName: selectedStudio,
        label: keyLabel || studio?.title || studio?.playlistName || "OBS Stream",
        expiresAfter: 0, // Never expires
      });

      const result = response.result || response;

      // Store the key config so user can copy it immediately
      setKeyConfigs((prev) => ({
        ...prev,
        [result.keyId]: {
          streamKey: result.streamKey,
          serverUrl: result.serverUrl,
        },
      }));
      setVisibleKeys((prev) => ({ ...prev, [result.keyId]: true }));

      notifier.success(t("streamKeys.created") || "Stream key created successfully");

      // Close dialog and refresh
      setCreateDialogOpen(false);
      setSelectedStudio("");
      setKeyLabel("");
      fetchKeys();
    } catch (error) {
      console.error("Error creating stream key:", error);
      notifier.error(error.message || t("streamKeys.createError") || "Failed to create stream key");
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!selectedKeyId) return;

    try {
      await ApiService.STREAM_KEYS_REVOKE(selectedKeyId);
      notifier.success(t("streamKeys.revoked") || "Stream key revoked successfully");

      // Remove from local state
      setKeyConfigs((prev) => {
        const newConfigs = { ...prev };
        delete newConfigs[selectedKeyId];
        return newConfigs;
      });
      setVisibleKeys((prev) => {
        const newVisible = { ...prev };
        delete newVisible[selectedKeyId];
        return newVisible;
      });

      fetchKeys();
    } catch (error) {
      console.error("Error revoking stream key:", error);
      notifier.error(error.message || t("streamKeys.revokeError") || "Failed to revoke stream key");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedKeyId(null);
    }
  };

  const handleShowKey = async (keyId) => {
    if (visibleKeys[keyId]) {
      setVisibleKeys((prev) => ({ ...prev, [keyId]: false }));
      return;
    }

    if (keyConfigs[keyId]) {
      setVisibleKeys((prev) => ({ ...prev, [keyId]: true }));
      return;
    }

    try {
      setLoadingConfig((prev) => ({ ...prev, [keyId]: true }));
      const response = await ApiService.STREAM_KEYS_GET_CONFIG(keyId);
      const data = response.result || response;
      setKeyConfigs((prev) => ({
        ...prev,
        [keyId]: {
          streamKey: data.streamKey,
          serverUrl: data.serverUrl,
        },
      }));
      setVisibleKeys((prev) => ({ ...prev, [keyId]: true }));
    } catch (error) {
      console.error("Error fetching key config:", error);
      notifier.error(error.message || "Failed to load stream key");
    } finally {
      setLoadingConfig((prev) => ({ ...prev, [keyId]: false }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    notifier.success(`${label} ${t("streamKeys.copied") || "copied to clipboard"}`);
  };

  const isKeyExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date() > new Date(expiresAt);
  };

  const getStudioName = (channelId) => {
    const studio = studios.find((s) => s._id === channelId);
    return studio?.title || studio?.playlistName || channelId;
  };

  if (!obsAvailable && !loading) {
    return (
      <Box sx={{ padding: "0 22px" }}>
        <PageHeader />
        <Box sx={{ maxWidth: 800, mt: 2 }}>
          <Alert severity="warning">
            {t("streamKeys.notAvailable") || "OBS streaming is not currently available. Please contact support."}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "0 22px" }}>
      <PageHeader />
      <Box sx={{ maxWidth: 900 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <VideocamIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {t("streamKeys.title") || "OBS Streaming"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("streamKeys.subtitle") || "Stream to your studio using OBS"}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={loading || studios.length === 0}
          >
            {t("streamKeys.create") || "Create Key"}
          </Button>
        </Stack>

        {/* Server URL Card */}
        {serverUrl && (
          <Card sx={{ mb: 3, backgroundColor: "background.paper" }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t("streamKeys.serverUrlInfo") || "Server URL (same for all streams)"}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    backgroundColor: "action.hover",
                    padding: "8px 12px",
                    borderRadius: 1,
                    flex: 1,
                    wordBreak: "break-all",
                  }}
                >
                  {serverUrl}
                </Typography>
                <Tooltip title={t("copy") || "Copy"}>
                  <IconButton size="small" onClick={() => copyToClipboard(serverUrl, "Server URL")}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("streamKeys.autoStartInfo") ||
            "When you start streaming from OBS using a stream key, your livestream will automatically appear in the app. No need to start manually!"}
        </Alert>

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* No Keys Message */}
        {!loading && keys.length === 0 && (
          <Card sx={{ mb: 3, backgroundColor: "background.paper" }}>
            <CardContent>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                {t("streamKeys.noKeys") ||
                  "You don't have any stream keys yet. Create one to start streaming with OBS."}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Keys List */}
        {!loading && keys.length > 0 && (
          <>
            <Typography variant="h6" mb={2}>
              {t("streamKeys.existingKeys") || "Your Stream Keys"}
            </Typography>
            <Stack spacing={2}>
              {keys.map((key) => (
                <Card
                  key={key.keyId}
                  sx={{
                    backgroundColor: "background.paper",
                    opacity: isKeyExpired(key.expiresAt) ? 0.6 : 1,
                  }}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                          <Typography variant="h6">{key.label}</Typography>
                          {isKeyExpired(key.expiresAt) && (
                            <Chip
                              label={t("streamKeys.expired") || "Expired"}
                              size="small"
                              color="error"
                            />
                          )}
                        </Stack>

                        <Typography variant="body2" color="text.secondary" mb={1}>
                          {t("streamKeys.studio") || "Studio"}:{" "}
                          <strong>{getStudioName(key.channel)}</strong>
                        </Typography>

                        {/* Stream Key (hidden by default) */}
                        <Box mb={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {t("streamKeys.streamKeyLabel") || "Stream Key"}:
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                backgroundColor: "action.hover",
                                padding: "8px 12px",
                                borderRadius: 1,
                                flex: 1,
                                wordBreak: "break-all",
                              }}
                            >
                              {visibleKeys[key.keyId] && keyConfigs[key.keyId]
                                ? keyConfigs[key.keyId].streamKey
                                : "••••••••••••••••••••••••"}
                            </Typography>
                            <Tooltip
                              title={
                                visibleKeys[key.keyId] ? t("hide") || "Hide" : t("show") || "Show"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleShowKey(key.keyId)}
                                disabled={loadingConfig[key.keyId]}
                              >
                                {loadingConfig[key.keyId] ? (
                                  <CircularProgress size={20} />
                                ) : visibleKeys[key.keyId] ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            {visibleKeys[key.keyId] && keyConfigs[key.keyId] && (
                              <Tooltip title={t("copy") || "Copy"}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    copyToClipboard(keyConfigs[key.keyId].streamKey, "Stream Key")
                                  }
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>

                        <Stack direction="row" spacing={2}>
                          <Typography variant="caption" color="text.secondary">
                            {t("createdAt") || "Created"}: {getTimeDue(key.createdAt)}
                          </Typography>
                          {key.expiresAt && (
                            <Typography variant="caption" color="text.secondary">
                              {t("streamKeys.expires") || "Expires"}:{" "}
                              {new Date(key.expiresAt).toLocaleDateString()}
                            </Typography>
                          )}
                          {key.lastUsedAt && (
                            <Typography variant="caption" color="text.secondary">
                              {t("streamKeys.lastUsed") || "Last used"}: {getTimeDue(key.lastUsedAt)}
                            </Typography>
                          )}
                        </Stack>
                      </Box>

                      <Tooltip title={t("streamKeys.revoke") || "Revoke Key"}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedKeyId(key.keyId);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </>
        )}

        {/* Help Section */}
        <Card sx={{ mt: 3, backgroundColor: "background.paper" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("streamKeys.howToUse") || "How to use with OBS"}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ol style={{ paddingLeft: 20, margin: 0 }}>
                <li>{t("streamKeys.step1")}</li>
                <li>{t("streamKeys.step2")}</li>
                <li>{t("streamKeys.step3")}</li>
                <li>{t("streamKeys.step4")}</li>
                <li>{t("streamKeys.step5")}</li>
                <li>{t("streamKeys.step6")}</li>
                <li>{t("streamKeys.step7")}</li>
              </ol>
            </Typography>
          </CardContent>
        </Card>

        {/* OBS Plugin API Key Section */}
        <Box sx={{ mt: 5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <KeyIcon color="secondary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {t("obsApiKey.title") || "OBS Plugin API Key"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("obsApiKey.subtitle") || "Control your stream overlays and plugins from OBS"}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Info Alert */}
          <Alert severity="info" sx={{ mb: 3 }}>
            {t("obsApiKey.description") ||
              "Generate an API key to use with the Siki OBS plugin. This allows you to control song requests, skip songs, view tips, and more without leaving OBS."}
          </Alert>

          {/* Loading State */}
          {loadingObsApiKey && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {/* No API Key State */}
          {!loadingObsApiKey && !obsApiKey && (
            <Card sx={{ mb: 3, backgroundColor: "background.paper" }}>
              <CardContent>
                <Typography variant="body1" color="text.secondary" textAlign="center" mb={2}>
                  {t("obsApiKey.noKey") ||
                    "You don't have an OBS API key yet. Generate one to use with OBS plugins and overlays."}
                </Typography>
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    startIcon={generatingObsApiKey ? <CircularProgress size={20} /> : <AddIcon />}
                    onClick={handleGenerateObsApiKey}
                    disabled={generatingObsApiKey}
                  >
                    {generatingObsApiKey
                      ? (t("obsApiKey.generating") || "Generating...")
                      : (t("obsApiKey.generate") || "Generate API Key")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* API Key Display */}
          {!loadingObsApiKey && obsApiKey && (
            <Card sx={{ mb: 3, backgroundColor: "background.paper" }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t("obsApiKey.yourKey") || "Your OBS API Key"}
                    </Typography>

                    {/* API Key */}
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          backgroundColor: "action.hover",
                          padding: "8px 12px",
                          borderRadius: 1,
                          flex: 1,
                          wordBreak: "break-all",
                        }}
                      >
                        {obsApiKeyVisible ? obsApiKey.key : "••••••••••••••••••••••••••••••••"}
                      </Typography>
                      <Tooltip title={obsApiKeyVisible ? (t("hide") || "Hide") : (t("show") || "Show")}>
                        <IconButton size="small" onClick={() => setObsApiKeyVisible(!obsApiKeyVisible)}>
                          {obsApiKeyVisible ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {obsApiKeyVisible && (
                        <Tooltip title={t("copy") || "Copy"}>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(obsApiKey.key, "API Key")}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      {t("obsApiKey.createdAt") || "Created"}: {getTimeDue(obsApiKey.createdAt)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Tooltip title={t("obsApiKey.regenerate") || "Regenerate Key"}>
                      <IconButton
                        color="primary"
                        onClick={handleGenerateObsApiKey}
                        disabled={generatingObsApiKey}
                      >
                        {generatingObsApiKey ? <CircularProgress size={24} /> : <RefreshIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("obsApiKey.revoke") || "Revoke Key"}>
                      <IconButton
                        color="error"
                        onClick={() => setRevokeObsApiKeyDialogOpen(true)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Browser Source URLs */}
          {obsApiKey && (
            <Card sx={{ mb: 3, backgroundColor: "background.paper" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("obsApiKey.browserSources") || "OBS Browser Sources"}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {t("obsApiKey.browserSourcesDescription") ||
                    "Add these URLs as Browser Sources in OBS to control your stream and display overlays."}
                </Typography>

                {/* Controls Dock URL */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("obsApiKey.controlsDock") || "Controls Dock (skip, queue, requests)"}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      value={`${window.location.origin}/obs/controls?apiKey=${obsApiKey.key}`}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: "monospace", fontSize: "0.85rem" },
                      }}
                    />
                    <Tooltip title={t("copy") || "Copy"}>
                      <IconButton
                        onClick={() => copyToClipboard(
                          `${window.location.origin}/obs/controls?apiKey=${obsApiKey.key}`,
                          "Controls Dock URL"
                        )}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("obsApiKey.openWindow") || "Open in new window"}>
                      <IconButton
                        onClick={() => openDockWindow("controls", "siki-obs-controls", "width=420,height=600")}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>

                {/* Chat Dock URL */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("obsApiKey.chatDock") || "Chat Dock (view and manage chat)"}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      value={`${window.location.origin}/obs/chat?apiKey=${obsApiKey.key}`}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: "monospace", fontSize: "0.85rem" },
                      }}
                    />
                    <Tooltip title={t("copy") || "Copy"}>
                      <IconButton
                        onClick={() => copyToClipboard(
                          `${window.location.origin}/obs/chat?apiKey=${obsApiKey.key}`,
                          "Chat Dock URL"
                        )}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("obsApiKey.openWindow") || "Open in new window"}>
                      <IconButton
                        onClick={() => openDockWindow("chat", "siki-obs-chat", "width=400,height=700")}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Usage Instructions */}
          <Card sx={{ backgroundColor: "background.paper" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("obsApiKey.howToUse") || "How to use with OBS"}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <ol style={{ paddingLeft: 20, margin: 0 }}>
                  <li>{t("obsApiKey.step1") || "Generate your API key above"}</li>
                  <li>{t("obsApiKey.browserStep2") || "In OBS: View > Docks > Custom Browser Docks"}</li>
                  <li>{t("obsApiKey.browserStep3") || "Add a Controls dock with the Controls URL, and a Chat dock with the Chat URL"}</li>
                  <li>{t("obsApiKey.browserStep4") || "Start your livestream and control everything from OBS!"}</li>
                </ol>
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Create Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("streamKeys.createTitle") || "Create Stream Key"}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${alpha("#fff", 0.1)}` }}>
            <Typography variant="body2" color="text.secondary">
              {t("streamKeys.createDescription") ||
                "Select a studio to create a stream key for OBS."}
            </Typography>
          </Box>

          {loadingStudios ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading studios...
              </Typography>
            </Box>
          ) : studios.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                {t("streamKeys.noStudios") || "No studios found. Create one first."}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: 300, overflow: "auto" }}>
              {studios.map((studio, index) => (
                <React.Fragment key={studio._id}>
                  <ListItem
                    button
                    selected={selectedStudio === studio._id}
                    onClick={() => setSelectedStudio(studio._id)}
                    disabled={creatingKey}
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&.Mui-selected": {
                        backgroundColor: alpha("#FFD700", 0.15),
                        borderLeft: "3px solid #FFD700",
                      },
                      "&:hover": {
                        backgroundColor: alpha("#fff", 0.05),
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={studio.thumbnailImg?.filePath || studio.GIF?.filePath}
                        sx={{ width: 40, height: 40, borderRadius: 1 }}
                      >
                        {(studio.title || studio.playlistName)?.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={studio.title || studio.playlistName || studio._id}
                      secondary={`${studio.music?.length || 0} songs`}
                      primaryTypographyProps={{ fontWeight: selectedStudio === studio._id ? 600 : 400 }}
                    />
                  </ListItem>
                  {index < studios.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {selectedStudio && (
            <Box sx={{ p: 2, borderTop: `1px solid ${alpha("#fff", 0.1)}` }}>
              <TextField
                fullWidth
                size="small"
                label={t("streamKeys.labelField") || "Label (optional)"}
                value={keyLabel}
                onChange={(e) => setKeyLabel(e.target.value)}
                placeholder={t("streamKeys.labelPlaceholder") || "e.g., Main OBS, Backup"}
                disabled={creatingKey}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${alpha("#fff", 0.1)}`, p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creatingKey}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateKey}
            disabled={!selectedStudio || creatingKey}
          >
            {creatingKey && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {creatingKey ? (t("creating") || "Creating...") : (t("create") || "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title={t("streamKeys.revokeTitle") || "Revoke Stream Key"}
        message={
          t("streamKeys.revokeConfirm") ||
          "Are you sure you want to revoke this stream key? This action cannot be undone."
        }
        onConfirm={handleRevokeKey}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedKeyId(null);
        }}
      />

      {/* OBS API Key Revoke Confirmation Dialog */}
      <ConfirmDialog
        open={revokeObsApiKeyDialogOpen}
        title={t("obsApiKey.revokeTitle") || "Revoke OBS API Key"}
        message={
          t("obsApiKey.revokeConfirm") ||
          "Are you sure you want to revoke this API key? Any OBS overlays using this key will stop working."
        }
        onConfirm={handleRevokeObsApiKey}
        onCancel={() => setRevokeObsApiKeyDialogOpen(false)}
      />
    </Box>
  );
}
